/** The order the picker offers: the written piece first, then the media kinds, then link, with image last. */
export const ADD_ON_TYPES = ['article', 'video', 'audio', '3d', 'link', 'image'] as const;

export type AddOnType = (typeof ADD_ON_TYPES)[number];

export const DEFAULT_ADD_ON_TYPE = 'article';

const THREE_D_HOSTS = /sketchfab\.com|p3d\.in/i;

const AUDIO_HOSTS = /soundcloud\.com|spotify\.com|anchor\.fm|\.mp3|\.m4a|\.ogg|\.wav/i;

const VIDEO_HOSTS = /youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com|loom\.com|wistia|\.mp4|\.webm|\.mov/i;

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i;

/**
 * The type is only the icon: Farfalla stores it as a plain string and the viewer
 * renders every type alike, so it is read off the content and the picker overrides.
 */
export function inferTypeFrom({
    body = '',
    imageUrl = '',
    imageFile = null,
}: { body?: string; imageUrl?: string; imageFile?: File | null } = {}): AddOnType {
    // What the reader will do with the media decides the icon: an embed plays
    // where it is, a link takes the reader out of the book.
    const embed = firstEmbedUrlIn(body);
    if (embed) {
        if (THREE_D_HOSTS.test(embed)) return '3d';
        return AUDIO_HOSTS.test(embed) ? 'audio' : 'video';
    }

    const prose = textWithoutLinks(body);

    // Paste and save is the common gesture, and the editor stores a pasted URL
    // as a link, so a URL on its own lands on the same icon linked or not.
    const bareUrl = prose.match(/^https?:\/\/\S+$/i)?.[0];
    if (bareUrl) return typeOfUrl(bareUrl);

    const link = firstLinkUrlIn(body);
    // A link inside a written piece is still a piece.
    if (link) return prose ? DEFAULT_ADD_ON_TYPE : typeOfUrl(link);

    if (!prose && (imageUrl || imageFile || /<img/i.test(body))) return 'image';
    return DEFAULT_ADD_ON_TYPE;
}

function typeOfUrl(url: string): AddOnType {
    if (THREE_D_HOSTS.test(url)) return '3d';
    if (AUDIO_HOSTS.test(url)) return 'audio';
    if (VIDEO_HOSTS.test(url)) return 'video';
    if (IMAGE_EXTENSIONS.test(url)) return 'image';
    return 'link';
}

function firstEmbedUrlIn(html: string): string {
    return html.match(/<iframe[^>]+src=["']([^"']+)["']/i)?.[1] ?? '';
}

function firstLinkUrlIn(html: string): string {
    return html.match(/<a[^>]+href=["']([^"']+)["']/i)?.[1] ?? '';
}

/** The words an operator actually wrote, ignoring the link and embed markup. */
function textWithoutLinks(html: string): string {
    return html
        .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
